import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CalendarRange,
  ExternalLink,
  Languages,
  LogIn,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { getBrand } from "~/tenant";
import { t } from "@lingui/core/macro";

/**
 * Public portal page shown to unauthenticated visitors.
 * Directs them to login/signup or the public marketing site.
 */
export function PublicPortalPage() {
  const brand = getBrand();
  const marketingUrl = brand.marketingUrl;
  const marketingHost = marketingUrl?.replace(/^https?:\/\//, "");
  const year = new Date().getFullYear();

  const portalHighlights = [
    {
      title: t`Memberships`,
      description: t`Review your status, renewals, and required account details.`,
    },
    {
      title: t`Events`,
      description: t`Register, confirm details, and keep upcoming commitments in view.`,
    },
    {
      title: t`Teams`,
      description: t`Stay connected to the roster, records, and league activity that matter.`,
    },
  ];

  const trustNotes = [
    {
      Icon: ShieldCheck,
      label: t`Secure sign-in`,
      description: t`Email-first access with passkey, social, and MFA support when needed.`,
    },
    {
      Icon: Languages,
      label: t`Bilingual ready`,
      description: t`Built for English and French member communication from the start.`,
    },
    {
      Icon: CalendarRange,
      label: t`Built for league work`,
      description: t`Quiet, practical tools for registrations, memberships, and schedules.`,
    },
  ];

  return (
    <div className="bg-muted relative min-h-screen overflow-hidden">
      <div
        aria-hidden="true"
        className="from-background via-background/40 to-background pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(158,23,3,0.08),transparent_32%),linear-gradient(to_bottom,transparent,rgba(32,24,20,0.05))]"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 py-8 sm:px-8 lg:px-10">
        <header className="flex flex-col gap-4 border-b border-border/60 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.22em]">
              {brand.portalSubtitle ?? t`Member Portal`}
            </p>
            <h1 className="text-foreground text-3xl font-semibold tracking-tight sm:text-4xl">
              {brand.name}
            </h1>
          </div>

          {marketingUrl ? (
            <a
              href={marketingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm transition-colors"
            >
              {t`Visit ${marketingHost ?? marketingUrl}`}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </header>

        <main className="flex flex-1 items-center py-12 sm:py-16">
          <div className="grid w-full gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,420px)] lg:gap-14">
            <section className="order-2 max-w-3xl space-y-10 lg:order-none">
              <div className="space-y-5">
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-[0.18em]">
                  {t`Member access`}
                </p>
                <h2 className="text-foreground max-w-[16ch] text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
                  {t`Keep memberships, events, and team details in one practical place.`}
                </h2>
                <p className="text-muted-foreground max-w-[62ch] text-base leading-7 sm:text-lg">
                  {brand.description ??
                    t`This portal manages membership, reporting, and organization data.`}
                </p>
              </div>

              <dl className="grid gap-5 sm:grid-cols-3">
                {portalHighlights.map((highlight) => (
                  <div
                    key={highlight.title}
                    className="border-border/70 space-y-2 border-t pt-4"
                  >
                    <dt className="text-foreground text-sm font-medium">
                      {highlight.title}
                    </dt>
                    <dd className="text-muted-foreground text-sm leading-6">
                      {highlight.description}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="grid gap-3 sm:grid-cols-3">
                {trustNotes.map(({ Icon, label, description }) => (
                  <div
                    key={label}
                    className="border-border/70 bg-background/70 rounded-2xl border p-4"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="text-muted-foreground h-4 w-4" />
                      <p className="text-foreground text-sm font-medium">{label}</p>
                    </div>
                    <p className="text-muted-foreground mt-2 text-sm leading-6">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <aside className="border-border/70 bg-background/92 order-1 flex flex-col gap-6 rounded-[1.75rem] border p-6 shadow-sm sm:p-7 lg:order-none">
              <div className="space-y-3">
                <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em]">
                  {t`Continue to your account`}
                </p>
                <h2 className="text-foreground text-2xl font-semibold tracking-tight">
                  {t`Sign in to keep going`}
                </h2>
                <p className="text-muted-foreground text-sm leading-6">
                  {t`Most members already have an account. Start with the email tied to your registrations or membership history.`}
                </p>
              </div>

              <Button asChild className="w-full" size="lg">
                <Link to="/auth/login">
                  <LogIn className="mr-2 h-4 w-4" />
                  {t`Log in`}
                  <ArrowRight className="ml-auto h-4 w-4" />
                </Link>
              </Button>

              <div className="border-border/70 bg-muted/60 rounded-2xl border p-4">
                <p className="text-foreground text-sm font-medium">{t`New here?`}</p>
                <p className="text-muted-foreground mt-2 text-sm leading-6">
                  {t`Create your account first, then we’ll guide you through policy acceptance and profile details.`}
                </p>
                <Button asChild variant="outline" className="mt-4 w-full" size="lg">
                  <Link to="/auth/signup">
                    <UserPlus className="mr-2 h-4 w-4" />
                    {t`Create an account`}
                  </Link>
                </Button>
              </div>

              {marketingUrl ? (
                <div className="border-border/70 pt-2 text-sm">
                  <p className="text-muted-foreground">
                    {t`Looking for schedules, updates, or public information instead?`}
                  </p>
                  <a
                    href={marketingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground mt-3 inline-flex items-center gap-2 font-medium underline underline-offset-4"
                  >
                    {t`Visit ${marketingHost ?? marketingUrl}`}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ) : null}
            </aside>
          </div>
        </main>

        <footer className="text-muted-foreground pt-6 text-xs">
          {t`© ${year} ${brand.name}. All rights reserved.`}
        </footer>
      </div>
    </div>
  );
}
